/**
 * Main debt calculator service
 * Constitutional requirement: Average Daily Balance method with console logging
 */

import { DebtCalculationInput, PaymentScheduleEntry, DebtCalculationResult } from '../models/DebtModels.js'


/**
 * Calculate debt payoff using Average Daily Balance method (most common for credit cards)
 * @param {DebtCalculationInput} input - Calculation input parameters
 * @returns {DebtCalculationResult} Complete calculation result
 */
export function calculateCompoundInterest(input) {
  console.log('🧮 Starting Average Daily Balance calculation with input:', input)
  
  // Validate input
  const validation = input.validate()
  if (!validation.isValid) {
    console.log('❌ Input validation failed:', validation.errors)
    throw new Error(`Invalid input: ${validation.errors.join(', ')}`)
  }

  const paymentSchedule = []
  let currentBalance = input.balance
  let month = 1
  const dailyInterestRate = (input.interestRate / 100) / 365
  const maxMonths = 600 // 50 years maximum to prevent infinite loops

  console.log(`🔢 Daily interest rate: ${(dailyInterestRate * 100).toFixed(6)}%`)
  console.log(`💰 Starting balance: $${currentBalance.toFixed(2)}`)
  console.log(`💳 Monthly payment: $${input.monthlyPayment.toFixed(2)}`)

  while (currentBalance > 0.01 && month <= maxMonths) {
    const startingBalance = currentBalance
    
    // Calculate Average Daily Balance for the billing cycle
    const daysInMonth = 30.44
    const paymentDay = 15 // Assume payment made at the middle of cycle. (Between conservative and optimistic)
    // TODO: Add logic to handle different payment days if needed

    const averageDailyBalance = calculateMonthlyAverageBalance(
      startingBalance, 
      input.monthlyPayment, 
      daysInMonth,
      paymentDay
    )
    
    // Interest based on average daily balance (not compounding daily)
    const monthlyInterestCharge = averageDailyBalance * dailyInterestRate * daysInMonth
    
    // Apply interest and payment
    currentBalance += monthlyInterestCharge
    const actualPayment = Math.min(input.monthlyPayment, currentBalance)
    currentBalance -= actualPayment
    
    const principalPayment = actualPayment - monthlyInterestCharge
    const endingBalance = Math.max(0, currentBalance)
    
    const entry = new PaymentScheduleEntry(
      month,
      parseFloat(startingBalance.toFixed(2)),
      parseFloat(actualPayment.toFixed(2)),
      parseFloat(monthlyInterestCharge.toFixed(2)),
      parseFloat(principalPayment.toFixed(2)),
      parseFloat(endingBalance.toFixed(2))
    )
    
    paymentSchedule.push(entry)
    
    if (month % 12 === 0) {
      console.log(`📅 Year ${month / 12} complete - Balance: $${endingBalance.toFixed(2)}`)
    }
    
    month++
    
    // Check for payment too small to cover interest
    if (monthlyInterestCharge >= input.monthlyPayment && currentBalance > startingBalance) {
      console.log('⚠️ Monthly payment is too small to cover interest - debt will never be paid off')
      
      // Calculate minimum payment needed using average daily balance method
      const dailyRate = (input.interestRate / 100) / 365
      const daysInMonth = 30.44
      const monthlyInterestOnOriginalBalance = input.balance * dailyRate * daysInMonth
      const minPaymentForProgress = monthlyInterestOnOriginalBalance + (input.balance * 0.001) // Add 0.1% of balance for minimal principal progress
      
      console.log(`💡 Monthly interest on original balance: $${monthlyInterestOnOriginalBalance.toFixed(2)}`)
      console.log(`💡 Minimum payment for progress: $${minPaymentForProgress.toFixed(2)}`)
      
      // Return a special result object instead of throwing an error
      const result = new DebtCalculationResult(input, paymentSchedule)
      result.isPaymentTooSmall = true
      result.monthlyInterestCharge = monthlyInterestCharge
      result.minimumPaymentNeeded = minPaymentForProgress
      result.paymentTooSmallMessage = `Monthly payment ($${input.monthlyPayment.toFixed(2)}) is too small to cover interest charges ($${monthlyInterestCharge.toFixed(2)}). Minimum payment needed: $${minPaymentForProgress.toFixed(2)} to make progress on the debt.`
      
      return result
    }
  }

  if (month > maxMonths) {
    console.log('⚠️ Reached maximum calculation limit of 600 months')
  }

  const result = new DebtCalculationResult(input, paymentSchedule)
  result.calculateSummary()
  
  console.log(`✅ Average Daily Balance calculation complete - ${paymentSchedule.length} payments calculated`)
  console.log(`📊 Total interest: $${result.summary.totalInterest.toFixed(2)}`)
  
  return result
}


/**
 * Calculate average daily balance for a billing cycle
 * @param {number} startingBalance - Balance at start of cycle
 * @param {number} monthlyPayment - Payment amount
 * @param {number} daysInCycle - Days in billing cycle
 * @param {number} paymentDay - Day of cycle when payment is made (1-30)
 * @returns {number} Average daily balance
 */
function calculateMonthlyAverageBalance(startingBalance, monthlyPayment, daysInCycle, paymentDay = 30) {
  // Ensure payment day doesn't exceed cycle length
  const actualPaymentDay = Math.min(paymentDay, daysInCycle)
  
  // Days before payment: full starting balance
  const daysBeforePayment = actualPaymentDay
  
  // Days after payment: reduced balance
  const daysAfterPayment = daysInCycle - actualPaymentDay
  
  const balanceBeforePayment = startingBalance
  const balanceAfterPayment = Math.max(0, startingBalance - monthlyPayment)
  
  // Calculate weighted average based on days at each balance
  const totalBalance = (balanceBeforePayment * daysBeforePayment) + (balanceAfterPayment * daysAfterPayment)
  const averageBalance = totalBalance / daysInCycle
  
  console.log(`📊 Average Daily Balance: ${daysBeforePayment} days @ $${balanceBeforePayment.toFixed(2)} + ${daysAfterPayment} days @ $${balanceAfterPayment.toFixed(2)} = $${averageBalance.toFixed(2)} avg`)
  
  return averageBalance
}


/**
 * Used for "How long to pay off?" mode
 * @param {DebtCalculationInput} input - Calculation input parameters
 * @returns {DebtCalculationResult} Complete calculation result
 */
export function calculateDebtPayoff(input) {
  console.log('🎯 Starting debt payoff calculation with mode:', input.calculationMode)
  
  if (!(input instanceof DebtCalculationInput)) {
    //console.log('🔧 Converting input to DebtCalculationInput instance')
    input = DebtCalculationInput.fromJSON(input)
  }
  
  try {
    // Always use compound interest (credit card standard)
    const result = calculateCompoundInterest(input)
    
  
    return result
    
  } catch (error) {
    console.log('❌ Error during debt calculation:', error)
    throw error
  }
}

/**
 * Calculate required monthly payment for target payoff period using daily compounding
 * @param {number} balance - Current debt balance
 * @param {number} annualInterestRate - Annual interest rate (percentage)
 * @param {number} targetMonths - Target payoff period in months
 * @returns {DebtCalculationResult} Complete calculation result with required payment
 */
export function calculateRequiredPayment(balance, annualInterestRate, targetMonths) {
  console.log('🎯 Starting required payment calculation')
  console.log(`💰 Balance: $${balance}, Rate: ${annualInterestRate}%, Target: ${targetMonths} months`)
  
  // Validate inputs
  if (balance <= 0 || balance > 1000000) {
    throw new Error('Balance must be between $1 and $1,000,000')
  }
  if (annualInterestRate < 0 || annualInterestRate > 100) {
    throw new Error('Interest rate must be between 0% and 100%')
  }
  if (targetMonths < 1 || targetMonths > 1200) {
    throw new Error('Target months must be between 1 and 1200 (100 years)')
  }

  // For zero interest, simple division
  if (annualInterestRate === 0) {
    const requiredPayment = balance / targetMonths
    console.log(`📊 Zero interest required payment: $${requiredPayment.toFixed(2)}`)
    
    // Create input object for generating payment schedule
    const input = new DebtCalculationInput(balance, annualInterestRate, requiredPayment, 'monthlyPayment')
    return calculateCompoundInterest(input)
  }

  // Use iterative approach to find the required payment for daily compounding
  // Start with a reasonable estimate using monthly compounding formula
  const monthlyRate = (annualInterestRate / 100) / 12
  const numerator = balance * monthlyRate * Math.pow(1 + monthlyRate, targetMonths)
  const denominator = Math.pow(1 + monthlyRate, targetMonths) - 1
  let estimatedPayment = numerator / denominator

  console.log(`🔍 Initial estimate: $${estimatedPayment.toFixed(2)}`)

  // Refine using binary search to account for daily compounding
  let lowPayment = estimatedPayment * 0.9
  let highPayment = estimatedPayment * 1.2
  let requiredPayment = estimatedPayment
  const tolerance = 0.01 // $0.01 tolerance
  let iterations = 0
  const maxIterations = 50

  while (iterations < maxIterations) {
    // Test current payment amount
    const testInput = new DebtCalculationInput(balance, annualInterestRate, requiredPayment, 'monthlyPayment')
    const testResult = calculateCompoundInterest(testInput)
    
    const actualMonths = testResult.summary.totalMonths
    const monthsDifference = actualMonths - targetMonths

    console.log(`📊 Iteration ${iterations + 1}: Payment $${requiredPayment.toFixed(2)} → ${actualMonths} months (target: ${targetMonths})`)

    if (Math.abs(monthsDifference) <= 1) {
      // Close enough - within 1 month tolerance
      console.log(`✅ Required payment found: $${requiredPayment.toFixed(2)} for ${targetMonths} months`)
      return testResult
    }

    if (monthsDifference > 0) {
      // Takes too long, need higher payment
      lowPayment = requiredPayment
      requiredPayment = (requiredPayment + highPayment) / 2
    } else {
      // Pays off too quickly, can use lower payment
      highPayment = requiredPayment
      requiredPayment = (lowPayment + requiredPayment) / 2
    }

    iterations++
  }

  // If we couldn't converge, return the best estimate
  console.log(`⚠️ Convergence limit reached after ${maxIterations} iterations`)
  const finalInput = new DebtCalculationInput(balance, annualInterestRate, requiredPayment, 'monthlyPayment')
  return calculateCompoundInterest(finalInput)
}

